import { useContext, useState, useEffect } from 'react';
import { ArrowLeft, Calendar, Target, Users, FileText, CheckCircle, Clock, Edit, Trash2, Plus, Download, Eye, Play, XCircle, Award, RefreshCw } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../../../../context/AuthContext';
import { useToast } from '../../../../../../context/ToastContext';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import Pagination from '../../../../../../components/Pagination';
import auditProgramService from '../../../../../../services/audit_services/auditProgramService';
import auditPlanService from '../../../../../../services/audit_services/auditPlanService';
import exportAuditProgramService from '../../../../../../services/audit_services/export_pdf_services/exportAuditProgramService';

// Import workflow modals
import {
  CloseProgramModal,
  ReopenProgramModal,
  AchieveProgramModal,
  UnachieveProgramModal
} from './auditProgramModals';
import AddPartyModal from './auditProgramModals/AddPartyModal';

export default function AuditProgramDetails() {
  const { id } = useParams();
  const { authTokens, hasRole } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  const navigate = useNavigate();

  const [program, setProgram] = useState(null);
  const [statistics, setStatistics] = useState({
    total_audits: 0,
    in_progress_audits: 0,
    completed_audits: 0,
    pending_audits: 0,
    completion_rate: 0
  });
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditsLoading, setAuditsLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState({});
  
  // Modal states
  const [closeModal, setCloseModal] = useState(false);
  const [reopenModal, setReopenModal] = useState(false);
  const [achieveModal, setAchieveModal] = useState(false);
  const [unachieveModal, setUnachieveModal] = useState(false);
  const [addPartyModal, setAddPartyModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filter state - applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    status: '',
    ordering: 'newest'
  });

  // Filter state - temporary (before applying)
  const [tempFilters, setTempFilters] = useState({
    search: '',
    status: '',
    ordering: 'newest'
  });



  useEffect(() => {
    fetchProgramDetails();
  }, [id]);

  useEffect(() => {
    if (program) {
      fetchStatistics();
      fetchAuditPlans();
    }
  }, [program, currentPage, pageSize, appliedFilters]);

  const fetchProgramDetails = async () => {
    setLoading(true);
    
    try {
      const result = await auditProgramService.getAuditProgramDetail(authTokens.access, id);
      
      if (result.success) {
        setProgram(result.data);
      } else {
        showError(result.error || 'Error loading program details');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching program details:', error);
      showError('Error loading program details');
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const result = await auditProgramService.getProgramStatistics(authTokens.access, id);
      
      if (result.success) {
        setStatistics(result.data);
      } else {
        console.error('Error loading statistics:', result.error);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchAuditPlans = async () => {
    setAuditsLoading(true);
    
    try {
      const result = await auditProgramService.getProgramAuditPlans(
        authTokens.access,
        id,
        appliedFilters,
        { page: currentPage, page_size: pageSize }
      );
      
      if (result.success) {
        setAudits(result.data.results);
        setTotalPages(result.data.total_pages);
        setTotalCount(result.data.count);
      } else {
        showError(result.error || 'Error loading audit plans');
      }
      setAuditsLoading(false);
    } catch (error) {
      console.error('Error fetching audit plans:', error);
      showError('Error loading audit plans');
      setAuditsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(tempFilters);
    setCurrentPage(1); // Reset to first page when filters are applied
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      search: '',
      status: '',
      ordering: 'newest'
    };
    setTempFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
    setCurrentPage(1);
  };

  const handleProgramWorkflowAction = (action) => {
    switch (action) {
      case 'close':
        setCloseModal(true);
        break;
      case 'reopen':
        setReopenModal(true);
        break;
      case 'achieve':
        setAchieveModal(true);
        break;
      case 'unachieve':
        setUnachieveModal(true);
        break;
      default:
        break;
    }
  };

  const handleModalSuccess = () => {
    // Refresh program details to get updated status
    fetchProgramDetails();
    // Refresh statistics and audit plans
    fetchStatistics();
    fetchAuditPlans();
  };

  const handleRemoveParty = async (partyId, partyName) => {
    if (window.confirm(`Are you sure you want to remove ${partyName} from this audit program?`)) {
      try {
        const result = await auditProgramService.removePartyFromAuditProgram(
          authTokens.access,
          id,
          partyId
        );
        
        if (result.success) {
          showSuccess(result.data.detail || 'Party removed successfully');
          fetchProgramDetails(); // Refresh to update parties list
        } else {
          showError(result.error || 'Failed to remove party');
        }
      } catch (error) {
        console.error('Error removing party:', error);
        showError('An error occurred while removing party');
      }
    }
  };

  const handleWorkflowAction = async (action, planId) => {
    if (workflowLoading[planId]) return;
    
    setWorkflowLoading(prev => ({ ...prev, [planId]: true }));
    try {
      let result;
      if (action === 'release') {
        result = await auditPlanService.releaseAuditPlan(authTokens.access, planId);
      }
      
      if (result.success) {
        showSuccess(`Audit plan ${action}d successfully`);
        fetchAuditPlans(); // Refresh the list
      } else {
        showError(result.error || `Error ${action}ing audit plan`);
      }
    } catch (error) {
      console.error(`Error ${action}ing audit plan:`, error);
      showError(`Error ${action}ing audit plan`);
    }
    setWorkflowLoading(prev => ({ ...prev, [planId]: false }));
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when page size changes
  };



  const handleExportPDF = async () => {
    setExportLoading(true);
    
    try {
      const result = await exportAuditProgramService.exportAuditProgramPDF(authTokens.access, id);
      
      if (result.success) {
        showSuccess('PDF downloaded successfully');
      } else {
        showError(result.error || 'Error exporting PDF');
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showError('Error exporting PDF');
    } finally {
      setExportLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      opened: { bg: 'bg-green-100', text: 'text-green-800', label: 'Ouvert', icon: Target },
      closed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Clôturé', icon: XCircle },
      achieved: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Archivé', icon: Award },
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Actif', icon: Target },
      scheduled: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Planifié', icon: Clock },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Complété', icon: CheckCircle },
      in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'En cours', icon: Target }
    };

    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      auditor: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Auditor' },
      auditee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Auditee' },
      participant: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Participant' },
      approver: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Approver' }
    };

    const config = roleConfig[role] || roleConfig.participant;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des détails du programme...</p>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Programme non trouvé</p>
        <Link to="/dashboard/audit/programs" className="text-green-600 hover:underline mt-4 inline-block">
          Retour aux programmes
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={-1}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{program.title}</h1>
            <p className="text-sm text-gray-600 mt-1">Détails du programme d'audit</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {getStatusBadge(program.status)}
          <button
            onClick={handleExportPDF}
            disabled={exportLoading}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exportLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Export...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Exporter PDF
              </>
            )}
          </button>
          {hasRole('AuditManager') && program.status === 'opened' && (
            <div className="flex items-center space-x-2">
              <Link
                to={`/dashboard/audit/programs/${program.id}/edit`}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Link>
              
              {/* Workflow buttons based on program status */}
              {program.status === 'opened' && (
                <>
                  <button
                    onClick={() => handleProgramWorkflowAction('close')}
                    className="inline-flex items-center px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Clôturer
                  </button>
                  
                  <button
                    onClick={() => handleProgramWorkflowAction('achieve')}
                    className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Award className="h-4 w-4 mr-2" />
                    Archiver
                  </button>
                </>
              )}
            </div>
          )}
          
          {hasRole('AuditManager') && program.status !== 'opened' && (
            <div className="flex items-center space-x-2">
              <span
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-400 rounded-lg cursor-not-allowed"
                title={`Cannot edit program when status is ${program.status}`}
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </span>
              
              {/* Workflow buttons based on program status */}
              {program.status === 'closed' && (
                <>
                  <button
                    onClick={() => handleProgramWorkflowAction('achieve')}
                    className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Award className="h-4 w-4 mr-2" />
                    Archiver
                  </button>
                  
                  <button
                    onClick={() => handleProgramWorkflowAction('reopen')}
                    className="inline-flex items-center px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Rouvrir
                  </button>
                </>
              )}
              
              {program.status === 'achieved' && (
                <button
                  onClick={() => handleProgramWorkflowAction('unachieve')}
                  className="inline-flex items-center px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Désarchiver
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Target className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total des audits</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_audits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Clock className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">En cours</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.in_progress_audits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Complétés</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.completed_audits}</p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Target className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Taux de complétion</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.completion_rate}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Program Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations du programme</h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Calendar className="h-4 w-4 mr-2 text-green-600" />
                  Date de création
                </p>
                <p className="text-sm text-gray-900">{new Date(program.created_at).toLocaleDateString('fr-FR', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Objectif</p>
                <p className="text-sm text-gray-900">{program.objective}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Périmètre</p>
                <ul className="text-sm text-gray-900 space-y-1">
                  {program.scope.map((scopeItem, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-600 mr-2">•</span>
                      {scopeItem}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Normes de référence</p>
                <p className="text-sm text-gray-900">{program.reference}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Approbateurs</p>
                <div className="space-y-2">
                  {program.approvers.map((approver) => (
                    <div key={approver.id} className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-green-600">
                          {approver.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{approver.name}</p>
                        <p className="text-xs text-gray-500">{approver.role} • {approver.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Parties Involved */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                <Users className="h-4 w-4 mr-2 text-green-600" />
                Parties impliquées ({program.parties.length})
              </h3>
              {hasRole('AuditManager') && (
                <button
                  onClick={() => setAddPartyModal(true)}
                  className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter une partie
                </button>
              )}
            </div>
            <div className="space-y-3">
              {program.parties.map((party) => (
                <div key={party.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        {party.name.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{party.name}</p>
                      <p className="text-xs text-gray-500">{party.userRole} • {party.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getRoleBadge(party.partyRole)}
                    {/* {hasRole('AuditManager') && (
                      <button
                        onClick={() => handleRemoveParty(party.id, party.name)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Remove party"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )} */}
                  </div>
                </div>
              ))}
              {program.parties.length === 0 && (
                <p className="text-sm text-gray-500">Aucune partie assignée</p>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Métadonnées</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600">Créé par</p>
                <p className="text-sm text-gray-900 mt-1">{program.created_by}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Date de création</p>
                <p className="text-sm text-gray-900 mt-1">{new Date(program.created_at).toLocaleDateString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Dernière mise à jour</p>
                <p className="text-sm text-gray-900 mt-1">{new Date(program.updated_at).toLocaleDateString('fr-FR')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Associated Audits */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Audits associés ({totalCount})</h3>
          {hasRole('AuditManager') && program.status === 'opened' && (
            <Link
              to={`/dashboard/audit/plans/create?program=${program.id}`}
              className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un plan d'audit
            </Link>
          )}
          {hasRole('AuditManager') && program.status !== 'opened' && (
            <span
              className="inline-flex items-center px-3 py-2 bg-gray-400 text-white rounded-lg cursor-not-allowed"
              title={`Impossible d'ajouter des plans d'audit quand le statut du programme est ${program.status}`}
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un plan d'audit
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <input
                type="text"
                placeholder="Rechercher des audits..."
                value={tempFilters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={tempFilters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="in_progress">En cours</option>
                <option value="completed">Complété</option>
                <option value="closed">Clôturé</option>
                <option value="achieved">Archivé</option>
              </select>
            </div>
            <div>
              <select
                value={tempFilters.ordering}
                onChange={(e) => handleFilterChange('ordering', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="newest">Plus récent d'abord</option>
                <option value="oldest">Plus ancien d'abord</option>
              </select>
            </div>
          </div>
          
          {/* Filter Action Buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              Appliquer les filtres
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition-colors"
            >
              Effacer les filtres
            </button>
          </div>
        </div>

        {auditsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : audits.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Titre de l'audit
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Période
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut de publication
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Créé le
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {audits.map((audit) => (
                    <tr key={audit.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/dashboard/audit/plans/${audit.id}`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-900"
                        >
                          {audit.title}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {audit.start_date ? new Date(audit.start_date).toLocaleDateString('fr-FR') : 'Non commencé'} - {audit.end_date ? new Date(audit.end_date).toLocaleDateString('fr-FR') : 'En cours'}
                      </td>
                      <td className="px-4 py-3">{getStatusBadge(audit.status)}</td>
                      <td className="px-4 py-3">
                        {audit.is_released ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            Publié
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            Non publié
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(audit.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Release Button - Only for pending plans that are not released */}
                          {hasRole('AuditManager') && audit.status === 'pending' && !audit.is_released && (
                            <button
                              onClick={() => handleWorkflowAction('release', audit.id)}
                              disabled={workflowLoading[audit.id]}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Publier le plan d'audit"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          )}

                          {/* View Details */}
                          <Link
                            to={`/dashboard/audit/plans/${audit.id}`}
                            className="text-green-600 hover:text-green-900"
                            title="Voir les détails"
                          >
                            <Eye className="h-4 w-4" />
                          </Link>

                          {/* Edit - Only when program is opened AND audit plan is not closed/achieved */}
                          {hasRole('AuditManager') && program.status === 'opened' && !['closed', 'achieved'].includes(audit.status) && (
                            <Link
                              to={`/dashboard/audit/plans/${audit.id}/edit`}
                              className="text-green-600 hover:text-green-900"
                              title="Modifier le plan d'audit"
                            >
                              <Edit className="h-4 w-4" />
                            </Link>
                          )}
                          
                          {/* Disabled Edit - Show when conditions are not met */}
                          {hasRole('AuditManager') && (program.status !== 'opened' || ['closed', 'achieved'].includes(audit.status)) && (
                            <span
                              className="text-gray-400 cursor-not-allowed"
                              title={
                                program.status !== 'opened' 
                                  ? `Impossible de modifier le plan d'audit quand le statut du programme est ${program.status}`
                                  : `Impossible de modifier le plan d'audit quand le statut est ${audit.status === 'closed' ? 'clôturé' : 'archivé'}`
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              loading={auditsLoading}
              itemName="plans d'audit"
            />
          </>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun audit trouvé</p>
            {hasRole('AuditManager') && program.status === 'opened' && (
              <Link
                to={`/dashboard/audit/plans/create?program=${program.id}`}
                className="inline-flex items-center mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer le premier plan d'audit
              </Link>
            )}
            {hasRole('AuditManager') && program.status !== 'opened' && (
              <p className="text-gray-500 mt-2">
                Impossible de créer des plans d'audit quand le statut du programme est {program.status}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Workflow Modals */}
      <CloseProgramModal
        isOpen={closeModal}
        onClose={() => setCloseModal(false)}
        programId={id}
        programTitle={program?.title}
        onSuccess={handleModalSuccess}
      />
      
      <ReopenProgramModal
        isOpen={reopenModal}
        onClose={() => setReopenModal(false)}
        programId={id}
        programTitle={program?.title}
        onSuccess={handleModalSuccess}
      />
      
      <AchieveProgramModal
        isOpen={achieveModal}
        onClose={() => setAchieveModal(false)}
        programId={id}
        programTitle={program?.title}
        onSuccess={handleModalSuccess}
      />
      
      <UnachieveProgramModal
        isOpen={unachieveModal}
        onClose={() => setUnachieveModal(false)}
        programId={id}
        programTitle={program?.title}
        onSuccess={handleModalSuccess}
      />

      <AddPartyModal
        isOpen={addPartyModal}
        onClose={() => setAddPartyModal(false)}
        programId={id}
        onSuccess={handleModalSuccess}
      />
  
    </div>
  );
}